import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';

interface ScoringFormProps {
  /** 送信ハンドラ（商品名またはURL） */
  onSubmit: (input: string) => void;
  /** ローディング状態 */
  isLoading?: boolean;
}

/** @graph-connects none */
const PRESETS = [
  'リファ ファインバブル U シャワーヘッド',
  'SIXPAD パワーガンポケット',
  'SIXPAD フットフィット 3',
  'BELEGA セルキュア4T PLUS II',
  'ELECTRON デンキバリブラシ 2.0+BODY',
  'ミラブルzero シャワーヘッド',
  'YA-MAN メディリフト アクア',
  'パナソニック ナノケア ドライヤー EH-NA0J',
  'ルルド フェイスメイクエステ',
  'アイロボット ルンバ i7+',
];

/** @graph-connects none */
function isUrlInput(text: string): boolean {
  return /^https?:\/\//i.test(text.trim());
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business スコアリング入力フォーム。商品名またはURL入力とプリセットボタンでスコアリング実行を受け付け
 * @graph-connects Button [calls] ボタンコンポーネント呼び出し
 * @graph-connects Input [calls] 入力フィールドコンポーネント呼び出し
 */
export function ScoringForm({ onSubmit, isLoading = false }: ScoringFormProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');

  const isUrl = useMemo(() => isUrlInput(input), [input]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (input.trim()) {
      onSubmit(input.trim());
    }
  };

  const handlePreset = (name: string) => {
    setInput(name);
    onSubmit(name);
  };

  return (
    <Card variant="glass" className="animate-fade-in-up">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <Input
            label={t('scoring.inputLabel')}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
            }}
            placeholder={t('scoring.inputPlaceholder')}
            disabled={isLoading}
          />
          {isUrl && (
            <span className="absolute right-3 top-9 text-xs px-2 py-0.5 rounded-full bg-[var(--color-accent-subtle)] text-[var(--color-accent-primary)] border border-[var(--color-accent-muted)]">
              URL
            </span>
          )}
        </div>
        <Button type="submit" size="lg" fullWidth loading={isLoading} disabled={!input.trim()}>
          {t('scoring.run')}
        </Button>
      </form>

      {/* プリセットボタン */}
      <div className="mt-6">
        <p className="text-xs font-medium text-[var(--color-text-muted)] mb-3">
          {t('scoring.presets')}
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((name) => (
            <button
              key={name}
              onClick={() => {
                handlePreset(name);
              }}
              disabled={isLoading}
              className="
                px-3 py-1.5 text-xs rounded-full
                bg-[var(--color-accent-subtle)] text-[var(--color-text-secondary)]
                border border-[var(--color-border)]
                hover:bg-[var(--color-accent-muted)] hover:text-[var(--color-accent-primary)]
                hover:border-[var(--color-border-hover)]
                transition-all duration-[var(--transition-fast)]
                disabled:opacity-50 disabled:cursor-not-allowed
                cursor-pointer
              "
            >
              {name}
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}
